import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User, Profile, App, Connection, ConnectionPlan, Theme, Notification } from '../types';

interface AppState {
  // Auth state
  user: User | null;
  isAuthenticated: boolean;
  
  // UI state
  theme: Theme;
  sidebarOpen: boolean;
  notifications: Notification[];
  selectedApp: App | null;
  
  // Data state
  profiles: Profile[];
  selectedProfile: Profile | null;
  apps: App[];
  connections: Connection[];
  connectionPlan: ConnectionPlan | null;
  
  // Loading states
  isLoading: {
    apps: boolean;
    connections: boolean;
    profiles: boolean;
    auth: boolean;
  };
  
  // Errors
  errors: {
    apps: string | null;
    connections: string | null;
    profiles: string | null;
    auth: string | null;
  };
}

interface AppActions {
  // Auth actions
  setUser: (user: User | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  logout: () => void;
  
  // UI actions
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  setSelectedApp: (app: App | null) => void;
  
  // Data actions
  setProfiles: (profiles: Profile[]) => void;
  setSelectedProfile: (profile: Profile | null) => void;
  addProfile: (profile: Profile) => void;
  updateProfile: (id: string, updates: Partial<Profile>) => void;
  deleteProfile: (id: string) => void;
  
  setApps: (apps: App[]) => void;
  addApp: (app: App) => void;
  updateApp: (id: string, updates: Partial<App>) => void;
  
  setConnections: (connections: Connection[]) => void;
  addConnection: (connection: Connection) => void;
  updateConnection: (id: string, updates: Partial<Connection>) => void;
  removeConnection: (id: string) => void;
  
  setConnectionPlan: (plan: ConnectionPlan | null) => void;
  updateConnectionPlan: (updates: Partial<ConnectionPlan>) => void;
  
  // Loading actions
  setLoading: (key: keyof AppState['isLoading'], loading: boolean) => void;
  
  // Error actions
  setError: (key: keyof AppState['errors'], error: string | null) => void;
  clearErrors: () => void;
  
  // Reset actions
  reset: () => void;
}

const initialState: AppState = {
  // Auth state
  user: null,
  isAuthenticated: false,
  
  // UI state
  theme: 'system',
  sidebarOpen: true,
  notifications: [],
  selectedApp: null,
  
  // Data state
  profiles: [],
  selectedProfile: null,
  apps: [],
  connections: [],
  connectionPlan: null,
  
  // Loading states
  isLoading: {
    apps: false,
    connections: false,
    profiles: false,
    auth: false,
  },
  
  // Errors
  errors: {
    apps: null,
    connections: null,
    profiles: null,
    auth: null,
  },
};

export const useAppStore = create<AppState & AppActions>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        
        // Auth actions
        setUser: (user) => set({ user }),
        setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
        logout: () => set({ 
          user: null, 
          isAuthenticated: false, 
          profiles: [], 
          selectedProfile: null,
          connections: [],
          connectionPlan: null 
        }),
        
        // UI actions
        setTheme: (theme) => set({ theme }),
        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
        setSidebarOpen: (open) => set({ sidebarOpen: open }),
        setSelectedApp: (app) => set({ selectedApp: app }),
        addNotification: (notification) => {
          const id = Math.random().toString(36).substring(7);
          const timestamp = new Date().toISOString();
          set((state) => ({
            notifications: [
              { ...notification, id, timestamp, read: false },
              ...state.notifications
            ].slice(0, 50) // Keep only last 50 notifications
          }));
        },
        removeNotification: (id) => set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        })),
        markNotificationRead: (id) => set((state) => ({
          notifications: state.notifications.map(n => 
            n.id === id ? { ...n, read: true } : n
          )
        })),
        
        // Data actions
        setProfiles: (profiles) => set({ profiles }),
        setSelectedProfile: (profile) => set({ selectedProfile: profile }),
        addProfile: (profile) => set((state) => ({
          profiles: [...state.profiles, profile]
        })),
        updateProfile: (id, updates) => set((state) => ({
          profiles: state.profiles.map(p => p.id === id ? { ...p, ...updates } : p)
        })),
        deleteProfile: (id) => set((state) => ({
          profiles: state.profiles.filter(p => p.id !== id),
          selectedProfile: state.selectedProfile?.id === id ? null : state.selectedProfile
        })),
        
        setApps: (apps) => set({ apps }),
        addApp: (app) => set((state) => ({
          apps: [...state.apps, app]
        })),
        updateApp: (id, updates) => set((state) => ({
          apps: state.apps.map(a => a.id === id ? { ...a, ...updates } : a)
        })),
        
        setConnections: (connections) => set({ connections }),
        addConnection: (connection) => set((state) => ({
          connections: [...state.connections, connection]
        })),
        updateConnection: (id, updates) => set((state) => ({
          connections: state.connections.map(c => c.id === id ? { ...c, ...updates } : c)
        })),
        removeConnection: (id) => set((state) => ({
          connections: state.connections.filter(c => c.id !== id)
        })),
        
        setConnectionPlan: (plan) => set({ connectionPlan: plan }),
        updateConnectionPlan: (updates) => set((state) => ({
          connectionPlan: state.connectionPlan ? { ...state.connectionPlan, ...updates } : null
        })),
        
        // Loading actions
        setLoading: (key, loading) => set((state) => ({
          isLoading: { ...state.isLoading, [key]: loading }
        })),
        
        // Error actions
        setError: (key, error) => set((state) => ({
          errors: { ...state.errors, [key]: error }
        })),
        clearErrors: () => set({
          errors: {
            apps: null,
            connections: null,
            profiles: null,
            auth: null,
          }
        }),
        
        // Reset actions
        reset: () => set(initialState),
      }),
      {
        name: 'pipedream-mcp-app',
        partialize: (state) => ({
          theme: state.theme,
          sidebarOpen: state.sidebarOpen,
          selectedProfile: state.selectedProfile,
        }),
      }
    ),
    {
      name: 'pipedream-mcp-store',
    }
  )
);

// Selectors
export const useAuth = () => {
  const { user, isAuthenticated, setUser, setAuthenticated, logout } = useAppStore();
  return { user, isAuthenticated, setUser, setAuthenticated, logout };
};

export const useUI = () => {
  const { 
    theme, 
    sidebarOpen, 
    notifications, 
    selectedApp,
    setTheme, 
    toggleSidebar, 
    setSidebarOpen,
    addNotification, 
    removeNotification, 
    markNotificationRead,
    setSelectedApp
  } = useAppStore();
  return { 
    theme, 
    sidebarOpen, 
    notifications, 
    selectedApp,
    setTheme, 
    toggleSidebar, 
    setSidebarOpen,
    addNotification, 
    removeNotification, 
    markNotificationRead,
    setSelectedApp
  };
};

export const useProfiles = () => {
  const { 
    profiles, 
    selectedProfile, 
    setProfiles, 
    setSelectedProfile, 
    addProfile, 
    updateProfile, 
    deleteProfile 
  } = useAppStore();
  return { 
    profiles, 
    selectedProfile, 
    setProfiles, 
    setSelectedProfile, 
    addProfile, 
    updateProfile, 
    deleteProfile 
  };
};

export const useApps = () => {
  const { apps, setApps, addApp, updateApp, setLoading, setError } = useAppStore();
  
  const fetchApps = async () => {
    setLoading('apps', true);
    setError('apps', null);
    try {
      const { mockApi } = await import('../lib/mockData');
      const fetchedApps = await mockApi.getApps();
      setApps(fetchedApps);
    } catch (error) {
      setError('apps', (error as Error).message);
    } finally {
      setLoading('apps', false);
    }
  };
  
  return { apps, setApps, addApp, updateApp, fetchApps };
};

export const useConnections = () => {
  const { 
    connections, 
    setConnections, 
    addConnection, 
    updateConnection, 
    removeConnection,
    setLoading,
    setError
  } = useAppStore();
  
  const fetchConnections = async () => {
    setLoading('connections', true);
    setError('connections', null);
    try {
      const { mockApi } = await import('../lib/mockData');
      const fetchedConnections = await mockApi.getConnections();
      setConnections(fetchedConnections);
    } catch (error) {
      setError('connections', (error as Error).message);
    } finally {
      setLoading('connections', false);
    }
  };
  
  return { 
    connections, 
    setConnections, 
    addConnection, 
    updateConnection, 
    removeConnection,
    fetchConnections
  };
};

export const useConnectionPlan = () => {
  const { connectionPlan, setConnectionPlan, updateConnectionPlan } = useAppStore();
  return { connectionPlan, setConnectionPlan, updateConnectionPlan };
};

export const useLoading = () => {
  const { isLoading, setLoading } = useAppStore();
  return { isLoading, setLoading };
};

export const useErrors = () => {
  const { errors, setError, clearErrors } = useAppStore();
  return { errors, setError, clearErrors };
};